import type { Octokit } from "@octokit/rest";
import { addDays, differenceInDays, isBefore } from "date-fns";
import { getFreshDay, setDay } from "@/lib/persist/day-cache";

interface PaginateOptions<T> {
  octokit: Octokit;
  route: string; // e.g. "GET /repos/{owner}/{repo}/actions/runs"
  params: Record<string, unknown>;
  since: Date;
  until: Date;
  hardCap?: number; // default 10000
  /**
   * Called on every page response AND every slice switch.
   * `loaded` is the cumulative unique-ID count, `currentDate` is the ISO start
   * (YYYY-MM-DD) of the slice being fetched, and `total` is the best known
   * upper bound (usually the `total_count` of the first full-window response)
   * so the UI can render a % progress bar.
   */
  onProgress?: (loaded: number, currentDate?: string, total?: number) => void;
  /** key used to extract the array from the response body (default: "workflow_runs") */
  itemsKey?: string;
  /**
   * When provided, daily slices are read from / written to the per-day
   * IndexedDB cache. Lets an interrupted fetch resume on reload and makes
   * subsequent full-window requests incremental.
   */
  dayCache?: { owner: string; repo: string };
  /**
   * AbortSignal forwarded from the react-query queryFn context. When
   * triggered (e.g. the user switches repo mid-fetch), in-flight octokit
   * requests are aborted and the slice loops bail out at the next boundary.
   */
  signal?: AbortSignal;
}

function ensureNotAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new DOMException("The paginator was aborted", "AbortError");
  }
}

interface PageResult<T> {
  items: T[];
  totalCount: number;
  hitCap: boolean;
}

export interface PaginateResult<T> {
  items: T[];
  /**
   * True iff data was demonstrably lost — a slice that could not be sub-sliced
   * further (1-day window) still hit the GitHub API's 1000-per-query ceiling.
   * Recursive sub-slicing handles weekly → daily automatically to maximise
   * recovered data; `hitCap` only stays true for the pathological case where a
   * single day has >1000 runs.
   */
  hitCap: boolean;
}

const PER_PAGE = 100;
const MAX_PAGES_BEFORE_SLICE = 10; // GitHub's /actions/runs caps at ~1000 results
const SLICE_CONCURRENCY = 3;

function toIsoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

async function fetchWindow<T>(
  octokit: Octokit,
  route: string,
  baseParams: Record<string, unknown>,
  since: Date,
  until: Date,
  itemsKey: string,
  onPageLoaded: (loaded: number, totalCount: number) => void,
  alreadyLoaded: number,
  hardCap: number,
  signal: AbortSignal | undefined,
): Promise<PageResult<T>> {
  const items: T[] = [];
  let hitCap = false;
  let totalCount = 0;
  let running = alreadyLoaded;

  const created = `${toIsoDay(since)}..${toIsoDay(until)}`;
  const params = { ...baseParams, created, per_page: PER_PAGE };

  for (let page = 1; page <= MAX_PAGES_BEFORE_SLICE; page++) {
    ensureNotAborted(signal);
    // Octokit forwards `request.signal` to the underlying fetch — an
    // abort interrupts the in-flight HTTP request immediately.
    const res = await octokit.request(route, {
      ...params,
      page,
      request: { signal },
    });
    const data = res.data as Record<string, unknown>;
    const pageItems = (data[itemsKey] as T[] | undefined) ?? [];
    totalCount = (data.total_count as number | undefined) ?? totalCount;
    items.push(...pageItems);
    running += pageItems.length;
    onPageLoaded(running, totalCount);

    if (running >= hardCap) {
      hitCap = true;
      break;
    }
    if (pageItems.length < PER_PAGE) break;
    if (page === MAX_PAGES_BEFORE_SLICE && totalCount > page * PER_PAGE) {
      hitCap = true;
    }
  }

  return { items, totalCount, hitCap };
}

/**
 * Run async workers with bounded concurrency. Returns after every job finishes.
 */
async function runConcurrent(concurrency: number, jobs: Array<() => Promise<void>>): Promise<void> {
  let cursor = 0;
  async function worker() {
    while (true) {
      const idx = cursor++;
      if (idx >= jobs.length) return;
      const job = jobs[idx];
      if (job) await job();
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, jobs.length) }, () => worker()));
}

/**
 * Enumerate [start, end) slices of a given duration in days between `since`
 * and `until`. The last slice is clamped to `until`. Returns at least one slice.
 */
function enumerateSlices(since: Date, until: Date, durationDays: number): Array<{ start: Date; end: Date }> {
  const slices: Array<{ start: Date; end: Date }> = [];
  let cursor = new Date(since);
  while (isBefore(cursor, until)) {
    const nextEnd = addDays(cursor, durationDays);
    slices.push({ start: new Date(cursor), end: isBefore(nextEnd, until) ? nextEnd : until });
    cursor = addDays(cursor, durationDays);
  }
  return slices;
}

/**
 * Paginate a GitHub list endpoint. Recovers as much data as possible by
 * falling back from full-window → weekly slices → daily slices when a slice
 * hits GitHub's ~1000-per-query cap.
 */
export async function paginateWithTimeSlice<T>(opts: PaginateOptions<T>): Promise<PaginateResult<T>> {
  const {
    octokit,
    route,
    params,
    since,
    until,
    hardCap = 10000,
    onProgress,
    itemsKey = "workflow_runs",
    dayCache,
    signal,
  } = opts;

  const collected: T[] = [];
  const seen = new Set<unknown>();
  let loaded = 0;
  // Best known global total (from the first full-window response). Sticky:
  // once set, later slice-level totals never overwrite it — they're per-slice,
  // not global.
  let globalTotal: number | undefined;

  function pushSliceItems(items: T[]) {
    for (const item of items) {
      const id = (item as { id?: unknown }).id;
      if (id !== undefined) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      collected.push(item);
    }
  }

  function reportPage(day: string | undefined, running: number, pageTotal: number) {
    if (globalTotal === undefined && pageTotal > 0) globalTotal = pageTotal;
    loaded = Math.max(loaded, running);
    const effective = seen.size > 0 ? seen.size : loaded;
    onProgress?.(effective, day, globalTotal);
  }

  /**
   * Distribute a flat result list into the per-day cache. Works for any T
   * with a `created_at` ISO string — all GitHub workflow-run payloads do.
   * Each day in [rangeStart, rangeEnd] gets an entry (including empty
   * days), so a subsequent request covering those days can short-circuit.
   * Defaults to the outer [since, until] if no range supplied.
   */
  async function writeDaysToCache(items: T[], rangeStart?: Date, rangeEnd?: Date) {
    if (!dayCache) return;
    const start = rangeStart ?? since;
    const end = rangeEnd ?? until;
    const days = enumerateSlices(start, end, 1);
    const buckets = new Map<string, T[]>();
    for (const day of days) {
      buckets.set(toIsoDay(day.start), []);
    }
    for (const item of items) {
      const createdAt = (item as { created_at?: string }).created_at;
      if (!createdAt) continue;
      const dayKey = createdAt.slice(0, 10);
      const list = buckets.get(dayKey);
      if (list) list.push(item);
    }
    await Promise.all(
      Array.from(buckets.entries()).map(([day, dayItems]) =>
        setDay(dayCache.owner, dayCache.repo, day, dayItems, false),
      ),
    );
  }

  /**
   * Fetch a multi-day slice (e.g. a week), using the day cache when every
   * day of the range is already cached fresh. On miss, falls through to a
   * single `fetchWindow` call for the whole range and writes each day of
   * the result to the cache so the next request short-circuits.
   */
  async function fetchRangeWithCache(
    rangeStart: Date,
    rangeEnd: Date,
  ): Promise<{ items: T[]; hitCap: boolean }> {
    if (dayCache) {
      const days = enumerateSlices(rangeStart, rangeEnd, 1);
      const dayEntries = await Promise.all(
        days.map((d) =>
          getFreshDay<T>(dayCache.owner, dayCache.repo, toIsoDay(d.start)),
        ),
      );
      if (dayEntries.every((e) => e !== null)) {
        const items: T[] = [];
        let hitCap = false;
        for (let i = 0; i < dayEntries.length; i++) {
          const entry = dayEntries[i];
          const day = days[i];
          if (!entry || !day) continue;
          if (entry.hitCap) hitCap = true;
          items.push(...entry.items);
          reportPage(toIsoDay(day.start), seen.size + items.length, 0);
        }
        return { items, hitCap };
      }
    }

    const sliceStart = toIsoDay(rangeStart);
    const slice = await fetchWindow<T>(
      octokit,
      route,
      params,
      rangeStart,
      rangeEnd,
      itemsKey,
      (n, t) => reportPage(sliceStart, n, t),
      0,
      hardCap,
      signal,
    );

    // Persist the fetched range day-by-day so the next request covering
    // this week (or an overlapping longer window) can skip the API.
    if (dayCache && !slice.hitCap) {
      await writeDaysToCache(slice.items, rangeStart, rangeEnd);
    }

    return slice;
  }

  /**
   * Fetch a single 1-day slice, using the day cache when available.
   * Returns the slice result AND writes fresh results back to the cache.
   */
  async function fetchDayWithCache(
    dayStart: Date,
    dayEnd: Date,
  ): Promise<{ items: T[]; hitCap: boolean }> {
    const isoDay = toIsoDay(dayStart);

    if (dayCache) {
      const cached = await getFreshDay<T>(dayCache.owner, dayCache.repo, isoDay);
      if (cached) {
        // Report the cached items as "loaded" so the progress bar advances.
        reportPage(isoDay, cached.items.length, 0);
        return { items: cached.items, hitCap: cached.hitCap };
      }
    }

    const slice = await fetchWindow<T>(
      octokit,
      route,
      params,
      dayStart,
      dayEnd,
      itemsKey,
      (n, t) => reportPage(isoDay, n, t),
      0,
      hardCap,
      signal,
    );

    if (dayCache) {
      await setDay(dayCache.owner, dayCache.repo, isoDay, slice.items, slice.hitCap);
    }

    return slice;
  }

  // ── Step 0: if every day of the window is already cached fresh, assemble
  //           from cache and skip the API entirely. This is the big win for
  //           reloads and for overlapping windows (7d → 30d reuses 7 days).
  if (dayCache) {
    const allDays = enumerateSlices(since, until, 1);
    const entries = await Promise.all(
      allDays.map((day) =>
        getFreshDay<T>(dayCache.owner, dayCache.repo, toIsoDay(day.start)),
      ),
    );
    if (entries.every((e) => e !== null)) {
      let anyHitCap = false;
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const day = allDays[i];
        if (!entry || !day) continue;
        if (entry.hitCap) anyHitCap = true;
        pushSliceItems(entry.items);
        reportPage(toIsoDay(day.start), seen.size, seen.size);
      }
      return { items: collected.slice(0, hardCap), hitCap: anyHitCap };
    }
  }

  // ── Step 1: try the full window first.
  const firstPass = await fetchWindow<T>(
    octokit,
    route,
    params,
    since,
    until,
    itemsKey,
    (n, t) => reportPage(toIsoDay(since), n, t),
    0,
    hardCap,
    signal,
  );

  if (!firstPass.hitCap) {
    // Populate the day cache so subsequent reloads hit Step 0 above.
    await writeDaysToCache(firstPass.items);
    return { items: firstPass.items, hitCap: false };
  }

  // Full window hit the cap — but the 1000 runs we already got back are
  // real data (the most recent 1000 in the window). Seed `collected` with
  // them so even a partial / aborted Step 2 still surfaces those weeks in
  // the dashboard. Step 2's weekly slicing will backfill the older gaps,
  // and `seen` dedupes any overlap by id.
  pushSliceItems(firstPass.items);
  loaded = 0;

  // ── Step 2: coarse slicing (weekly for long windows, daily for short ones).
  const totalDays = Math.max(1, differenceInDays(until, since));
  const coarseDays = totalDays > 14 ? 7 : 1;
  const coarseSlices = enumerateSlices(since, until, coarseDays);

  // Track slices that still hit cap AND have room to split further — those
  // get a second pass with daily granularity.
  const needsDailyRefinement: Array<{ start: Date; end: Date }> = [];
  let unrecoverable = false;

  await runConcurrent(
    SLICE_CONCURRENCY,
    coarseSlices.map((win) => async () => {
      ensureNotAborted(signal);
      const durationDays = Math.max(1, differenceInDays(win.end, win.start));

      // 1-day coarse slice → goes through the day cache directly.
      if (durationDays === 1) {
        const slice = await fetchDayWithCache(win.start, win.end);
        if (slice.hitCap) unrecoverable = true;
        pushSliceItems(slice.items);
        return;
      }

      // Weekly slice → cache-aware: skip the API when every day is fresh,
      // otherwise fetch once and persist day-by-day so subsequent requests
      // (different windowDays, reload, etc.) hit the cache. If it still
      // hits cap we defer to the refinement step which slices by day.
      const slice = await fetchRangeWithCache(win.start, win.end);
      if (slice.hitCap) {
        needsDailyRefinement.push(win);
        return;
      }
      pushSliceItems(slice.items);
    }),
  );

  // ── Step 3: daily refinement for weekly slices that hit cap — each day
  //            goes through the cache layer.
  if (needsDailyRefinement.length > 0) {
    const dailySlices: Array<{ start: Date; end: Date }> = [];
    for (const coarse of needsDailyRefinement) {
      dailySlices.push(...enumerateSlices(coarse.start, coarse.end, 1));
    }

    await runConcurrent(
      SLICE_CONCURRENCY,
      dailySlices.map((win) => async () => {
        ensureNotAborted(signal);
        const slice = await fetchDayWithCache(win.start, win.end);
        if (slice.hitCap) unrecoverable = true;
        pushSliceItems(slice.items);
      }),
    );
  }

  const truncated = collected.length >= hardCap;
  return {
    items: collected.slice(0, hardCap),
    hitCap: unrecoverable || truncated,
  };
}
